import { apiClient } from '@/core/api/client';
import type {
  CreateManualReferralPayload,
  ManualReferralEntitySearchParams,
  ManualReferralEntitySearchResponse,
  ManualReferralPrefill,
  ManualReferralResponse,
  UpdateManualReferralPayload,
} from '../types/manual-referral.type';
import type {
  BulkReferralFileError,
  BulkReferralResponseDto,
  BulkReferralUploadPayload,
  ManualReferralImportBatch,
} from '../types/bulk-referral.type';

const BASE_PATH = '/admin/referrals/manual';

export const searchManualReferralEntities = async (
  params: ManualReferralEntitySearchParams,
): Promise<ManualReferralEntitySearchResponse> => {
  const response = await apiClient.get<ManualReferralEntitySearchResponse>(
    `${BASE_PATH}/entity-search`,
    { params },
  );
  return response.data;
};

export const createManualReferral = async (
  payload: CreateManualReferralPayload,
): Promise<ManualReferralResponse> => {
  const response = await apiClient.post<ManualReferralResponse>(BASE_PATH, payload);
  return response.data;
};

// Feature 117: edit modal prefill + full-payload PATCH.
export const fetchManualReferralForEdit = async (id: string): Promise<ManualReferralPrefill> => {
  const response = await apiClient.get<ManualReferralPrefill>(`${BASE_PATH}/${id}`);
  return response.data;
};

export const updateManualReferral = async (
  id: string,
  payload: UpdateManualReferralPayload,
): Promise<ManualReferralResponse> => {
  const response = await apiClient.patch<ManualReferralResponse>(`${BASE_PATH}/${id}`, payload);
  return response.data;
};

// Feature 101: manually connect a provisional professional contact to an account.
export const linkManualProfessionalContact = async (
  contactId: string,
  userId: string,
): Promise<void> => {
  await apiClient.post(`${BASE_PATH}/contacts/${contactId}/link`, { userId });
};

/** Rejected `apiClient` error shape (see `frontend/src/core/api/client.ts`). */
type ApiClientError = { fileErrors?: unknown };

const isFileLevelRejection = (error: unknown): error is { fileErrors: BulkReferralFileError[] } =>
  Array.isArray((error as ApiClientError)?.fileErrors);

export const uploadBulkReferrals = async (
  payload: BulkReferralUploadPayload,
): Promise<BulkReferralResponseDto> => {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('dryRun', String(payload.dryRun));

  try {
    const response = await apiClient.post<BulkReferralResponseDto>(
      `${BASE_PATH}/bulk`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  } catch (error) {
    // A file-level rejection (spec §7.1) is a 4xx with `{ fileErrors }`, no
    // `rows`/`summary` — a legitimate result for the admin to fix and
    // re-upload, not an exceptional failure. Normalize it into the same
    // resolved `BulkReferralResponseDto` shape a row-level (200) response
    // has, so callers can tell the two apart on `fileErrors.length` alone
    // instead of `isError` vs. `isSuccess`. Anything else (network/server
    // error) rethrows for the caller's `onError`.
    if (isFileLevelRejection(error)) {
      return {
        dryRun: payload.dryRun,
        allValid: false,
        fileErrors: error.fileErrors,
        rows: [],
        summary: {
          totalRows: 0,
          resolvedRows: 0,
          errorRows: 0,
          newContacts: 0,
          reusedContacts: 0,
          invitesToSend: 0,
        },
      };
    }
    throw error;
  }
};

export const fetchImportHistory = async (): Promise<ManualReferralImportBatch[]> => {
  const response = await apiClient.get<ManualReferralImportBatch[]>(`${BASE_PATH}/import-batches`);
  return response.data;
};

// Feature 117: soft delete; the optional reason only lands in the audit log.
export const deleteManualReferral = async (id: string, reason?: string): Promise<void> => {
  const trimmed = reason?.trim();
  await apiClient.delete(`${BASE_PATH}/${id}`, { data: trimmed ? { reason: trimmed } : {} });
};

export const adminManualReferralsApi = {
  searchManualReferralEntities,
  createManualReferral,
  fetchManualReferralForEdit,
  updateManualReferral,
  linkManualProfessionalContact,
  uploadBulkReferrals,
  fetchImportHistory,
  deleteManualReferral,
};
